import React, {useState} from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, Divider, FormControl, FormControlLabel,
    IconButton, InputLabel, MenuItem, Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import {Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {KeyboardArrowDown, KeyboardArrowUp} from "@mui/icons-material";
import {useAuth} from "../../auth/AuthContext";
import {
    fetchEntities,
    fetchMetricIngest,
    fetchMetricNormalizationRules,
    hasNewRelicKey
} from "../../utils/newRelicDataUtil";
import {useEntityOrder} from "../../utils/useEntityOrder";
import {optimizationIngestSchedulerApi} from "../../utils/optimizationIngestSchedulerUtil";
import appTheme from "../../theme";
import {sx} from "./styles";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useNavigate} from "react-router-dom";
import {toolUsageApi, ToolUsageDto} from "../../utils/toolUsageUtil";
import moment from 'moment-timezone';
import { getMe } from "../../utils/meUtil";

const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export default function OptimizationIngestScheduler() {
    const {getAccessToken, } = useAuth();

    const [usage, setUsage] = useState<ToolUsageDto>();
    const [hasKey, setHasKey] = React.useState<boolean | null>(null);
    const [entities, setEntities] = React.useState<any[]>([]);
    const [selectedGuid, setSelectedGuid] = React.useState("");
    const [from, setFrom] = React.useState(() => isoDaysAgo(7));
    const [to, setTo] = React.useState(() => new Date().toISOString());
    const [series, setSeries] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    // const [rules, setRules] = React.useState<any[]>([]); // A list of metric normalization rules for the selected entity

    const [schedules, setSchedules] = React.useState<any[]>([]);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [form, setForm] = React.useState({enable: "02:00", disable: "04:00", days: [] as string[], ruleId:"", timezone: ''});
    const [userDefaultTz, setUserDefaultTz] = useState('');
    const [timezones, setTimezones] = useState<string[]>([]);
    const [customTz, setCustomTz] = useState(false);

    const {orderedEntities, moveUp, moveDown} = useEntityOrder(entities, "ingest.entityOrder");
    const nav = useNavigate();

    // Load New Relic key + entities
    React.useEffect(() => {
        const tzList = moment.tz.names();
        setTimezones(tzList.sort());

        (async () => {
            const token = await getAccessToken?.();
            const ok = token ? await hasNewRelicKey(token) : false;
            setHasKey(ok);
            if (!ok) return;
            const raw = await fetchEntities(token!);

            setEntities(raw?.data?.actor?.entitySearch?.results?.entities ?? []);

            try {
                const me = await getMe(token!);
                const defaultTz = me.defaultTimezone || moment.tz.guess() || 'America/New_York';
                setUserDefaultTz(defaultTz);
                setForm(prev => ({ ...prev, timezone: defaultTz }));
            } catch {}

            await loadUsage(token!);
        })();
    }, [getAccessToken]);

    // Load current tool usage (the number of schedules that the user has
    // subscribed to, and how many they've made so far.)
    const loadUsage = async (token: string) => {
        const toolUsageDto = await toolUsageApi.getToolUsage("OPTIMIZATION_INGEST_SCHEDULER", token!);
        setUsage(toolUsageDto);
    }

    // Load metric chart
    const loadChart = async () => {
        if (!selectedGuid) return;
        setLoading(true);
        try {
            const token = await getAccessToken?.();
            const raw = await fetchMetricIngest(selectedGuid, from, to, "1h", token!);
            setSeries((raw?.data?.actor?.entity?.nrdbQuery?.results ?? []).map((p: any) => ({
                ts: new Date((p.beginTimeSeconds || p.endTimeSeconds) * 1000).toISOString(),
                value: Number(p.sum ?? p.count ?? 0),
            })));
        } catch (e: any) {
            setError(e.message || "Failed");
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        if (hasKey && selectedGuid) loadChart();
    }, [selectedGuid, from, to, hasKey]);

    // Load schedules
    const loadSchedules = async () => {
        if (!selectedGuid) return;
        const token = await getAccessToken?.();
        const data = await optimizationIngestSchedulerApi.getMetricSchedules(selectedGuid, token);
        const name = entities.find(e => e.guid === selectedGuid)?.name;
        const list = Array.isArray(data) ? data.filter((s: any) => s.entityName === name) : [];
        setSchedules(list);
    };

    React.useEffect(() => {
        if (selectedGuid) loadSchedules();
    }, [selectedGuid, entities]);

    // Save
    const handleSave = async () => {
        if (!form.days.length || !selectedGuid) return;

        setLoading(true);

        try {
            const token = await getAccessToken?.();
            const entity = entities.find(e => e.guid === selectedGuid);
            if (!token || !entity) return;

            await optimizationIngestSchedulerApi.createMetricSchedule(
                [{
                    entityName: entity.name,
                    entityGuid: entity.guid,
                    enableTime: form.enable,
                    disableTime: form.disable,
                    scheduleDays: form.days,
                    active: true,
                    ...(form.ruleId && { ruleId: form.ruleId }),
                    ...(customTz && form.timezone && { timezone: form.timezone })  // Send only if custom
                }]
            , token);

            await loadUsage(token);
            await loadSchedules();
            setDialogOpen(false);
        } catch (e: any) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                "An error occurred. Please try again later.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    // Delete day
    const deleteDay = async (sched: any, day: string) => {
        const token = await getAccessToken?.();
        if (!token || !sched.uuid) return;
        await optimizationIngestSchedulerApi.deleteMetricScheduleDay(sched.uuid, day, token);
        await loadSchedules();
        await loadUsage(token);
    };

    const openScheduleDialog = async () => {
        if (!selectedGuid) return;

        setLoading(true);
        setError(null);
        try {

            // TODO: Uncomment the following when we are ready to allow users to select rules.
            // const token = await getAccessToken?.();
            // const raw = await fetchMetricNormalizationRules(selectedGuid, token!);
            // // Trust the backend — it already filtered for us
            // setRules(raw?.data?.actor?.account?.metricNormalization?.metricNormalizationRules ?? []);
            // setForm(prev => ({
            //     ...prev,
            //     ruleId: rules.length > 0 ? rules[0].id : ""
            // }));

            setDialogOpen(true);
        } catch (e: any) {
            setError("Failed to load metric normalization rules: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    const toUserTime = (iso: string) => iso ? new Date(iso).toLocaleString("sv", {timeZone: userDefaultTz || "America/New_York"}).slice(0, 16) : "";


    if (hasKey === null) return <Typography p={3}>Loading…</Typography>;
    if (!hasKey) {
        return (
            <Alert severity="info" sx={{m: 3, justifyContent:"center"}}>
                <Typography>
                    To use the Ingest Optimiser, please supply a New Relic User API Key to connect your account.
                </Typography>

                <Button variant="contained" onClick={() => nav("/newRelicIntegration")} sx={{mt:2, alignSelf: 'center'}}>
                    CONNECT NOW
                </Button>
            </Alert>
        );
    }

    return (
        <Box sx={{m: 3, gap: 3, display: "grid"}}>
            {error && <Alert severity="error">{error}</Alert>}

            <Card variant={"gradient" as any} sx={sx.titleCardSx}>
                <Box component="img" src="/tool_icons/OPTIMIZATION_INGEST_SCHEDULER.png" sx={sx.toolIcon}/>
                <Box sx={sx.titleSx}>
                    <Typography variant="h4">Optimization Ingest Scheduler</Typography>
                    <Typography variant="h6">{usage?.usedQty || "-"} / {usage?.subscribedQty || "-"} Schedules Used</Typography>
                </Box>
            </Card>

            <Box sx={sx.topRow}>
                <Card sx={sx.cardSx}>
                    <CardHeader title="Service (Entity)"/>
                    <CardContent>
                        {orderedEntities.map((e: any, i: number) => (
                            <Box key={e.guid}
                                 sx={{display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5}}>
                                <FormControlLabel
                                    control={<Checkbox size="small" checked={selectedGuid === e.guid}
                                                       onChange={() => setSelectedGuid(e.guid)}/>}
                                    label={e.name}
                                />
                                <Box>
                                    <IconButton color="primary" onClick={() => moveUp(e.guid)} disabled={i === 0}>
                                        <KeyboardArrowUp/>
                                    </IconButton>
                                    <IconButton color="primary" onClick={() => moveDown(e.guid)}
                                                disabled={i === orderedEntities.length - 1}><KeyboardArrowDown/></IconButton>
                                </Box>
                            </Box>
                        ))}
                        <Divider sx={{mt:1, mb:1}}/>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => nav("/ORPHANED_SCHEDULES")}
                            sx={{ textTransform: "none" }}
                        >
                            See Orphaned Schedules
                        </Button>
                    </CardContent>

                </Card>

                <Card sx={sx.cardSx}>
                    <CardHeader
                        title={`${entities.find(e => e.guid === selectedGuid)?.name ?? ""} – Ingest Removal Schedule`}/>
                    <CardContent>
                        {schedules.length === 0 ? (
                            <Typography color="text.secondary">No schedule set</Typography>
                        ) : (
                            schedules.map((s: any, i: number) => (
                                <Box key={i} sx={{mt: 2, p: 2, borderRadius: 1}}>
                                    <Typography fontWeight="medium">
                                        {s.enableTime} – {s.disableTime} ({s.timezone || "America/New_York"})
                                        {/*TODO: Uncomment the following line to display rules for each schedule when we are ready to allow users to select rules.*/}
                                        {/*<Typography color="primary" fontWeight="medium" component="span">&nbsp;Rule Number {s.ruleId}</Typography>*/}
                                    </Typography>
                                    <Box sx={{mt: 1, display: "flex", flexWrap: "wrap", gap: 1}}>
                                        {(s.scheduleDays || []).map((d: string) => (
                                            <Chip
                                                key={d}
                                                label={d.toUpperCase()}
                                                deleteIcon={<DeleteOutlineIcon/>}
                                                onDelete={() => deleteDay(s, d)}
                                                variant="outlined"
                                                sx={sx.buttonSx}
                                                disabled={isRunning(s, d)}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            ))
                        )}
                        <Button color="error" onClick={openScheduleDialog} disabled={!selectedGuid || loading} sx={{mt: 2}}>
                            {loading ? "Loading rules…" : "Set Schedule"}
                        </Button>
                    </CardContent>
                </Card>
            </Box>

            <Alert severity="info">
                The chart below is displayed in your default timezone, which is {userDefaultTz || "America/New_York"}.
                <br/>
                You may change this in your user settings.
            </Alert>

            {/* Chart */}
            <Card sx={sx.cardSx}>
                <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" color="primary">Metric Ingestion</Typography>
                    <Box sx={{gap: 2, display: "flex"}}>
                        <TextField
                            size="small"
                            type="datetime-local"
                            label="From"
                            value={toUserTime(from)}
                            onChange={e => setFrom(new Date(e.target.value + ":00-05:00").toISOString())}
                            InputLabelProps={{shrink: true}}
                            sx={sx.dateTimeSx}
                        />
                        <TextField
                            size="small"
                            type="datetime-local"
                            label="To"
                            value={toUserTime(to)}
                            onChange={e => setTo(new Date(e.target.value + ":00-05:00").toISOString())}
                            InputLabelProps={{shrink: true}}
                            sx={sx.dateTimeSx}
                        />
                        <Button sx={sx.buttonSx} onClick={loadChart} disabled={loading}>
                            {loading ? "…" : "Refresh"}
                        </Button>
                    </Box>
                </Box>
                <CardContent>
                    <Box height={360} >
                        <ResponsiveContainer>
                            <AreaChart data={series}>
                                <XAxis dataKey="ts" tickFormatter={toUserTime}/>
                                <YAxis/>
                                <Tooltip labelFormatter={toUserTime} contentStyle={{color: appTheme.palette.secondary.main}}/>
                                <Area type="monotone" dataKey="value" stroke={appTheme.palette.primary.main}
                                      fill={appTheme.palette.primary.main} fillOpacity={0.6}/>
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                </CardContent>
            </Card>

            {/* Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Set Schedule (Eastern Time)</DialogTitle>
                <DialogContent>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={1} mb={3}>
                        <TextField label="Enable Time (ET)" type="time" value={form.enable}
                                   onChange={e => setForm(f => ({...f, enable: e.target.value}))} sx={sx.dateTimeSx}/>
                        <TextField label="Disable Time (ET)" type="time" value={form.disable}
                                   onChange={e => setForm(f => ({...f, disable: e.target.value}))} sx={sx.dateTimeSx}/>
                    </Box>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Box sx={{mt: 3, display: "flex", justifyContent: "space-around"}}>
                        <ToggleButtonGroup
                            value={form.days}
                            onChange={(_, v) => setForm(f => ({...f, days: v || []}))}
                            color="primary"
                            sx={{bgcolor: "white"}}
                        >
                            {DAYS.map(d => (
                                <ToggleButton color="primary" key={d}
                                              value={d}>{d.toUpperCase().slice(0, 3)}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    <FormControlLabel
                        control={<Checkbox checked={customTz} onChange={e => setCustomTz(e.target.checked)} />}
                        label="Run on custom timezone"
                    />
                    {customTz && (
                        <FormControl fullWidth>
                            <Autocomplete
                                value={form.timezone || userDefaultTz}
                                onChange={(e, newValue) => setForm(f => ({...f, timezone: newValue || ''}))}
                                options={timezones}
                                renderInput={(params) => <TextField {...params} label="Timezone" />}
                                disabled={loading}
                            />
                        </FormControl>
                    )}


                    {/*TODO: Uncomment the rule selection dropdown when we are ready to allow users to select rules. */}
                    {/*<FormControl fullWidth sx={{mt: 2}}>*/}
                    {/*    <InputLabel id="rule-label">Metric Normalization Rule</InputLabel>*/}
                    {/*    <Select*/}
                    {/*        labelId="rule-label"*/}
                    {/*        value={form.ruleId}*/}
                    {/*        label="Metric Normalization Rule"*/}
                    {/*        onChange={e => setForm(f => ({...f, ruleId: e.target.value}))}*/}
                    {/*    >*/}
                    {/*        {rules.length === 0 ? (*/}
                    {/*            <MenuItem disabled>No applicable rules found</MenuItem>*/}
                    {/*        ) : (*/}
                    {/*            rules.map(rule => (*/}
                    {/*                <MenuItem key={rule.id} value={rule.id}>*/}
                    {/*                    IF {rule.matchExpression} THEN {rule.action} {rule.replacement}*/}
                    {/*                    <Typography color="primary">&nbsp;{rule.notes}</Typography>*/}
                    {/*                </MenuItem>*/}
                    {/*            ))*/}
                    {/*        )}*/}
                    {/*    </Select>*/}
                    {/*</FormControl>*/}

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={!form.days.length}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

const isoDaysAgo = (d: number) => {
    const x = new Date();
    x.setDate(x.getDate() - d);
    return x.toISOString();
};


/**
 * Returns true if the given schedule is currently active in its Timezone.
 * @param sched - schedule object from backend
 */
const isRunning = (
    sched: {
        enableTime?: string;
        disableTime?: string;
        scheduleDays?: string[];
        timezone?: string;
    },
    day: string
): boolean => {
    const now = new Date();
    const etNow = new Date(now.toLocaleString("en-US", {timeZone: sched.timezone || "America/New_York"}));

    const todayET = DAYS[etNow.getDay()]; // "sun", "mon", etc.
    const currentMins = etNow.getHours() * 60 + etNow.getMinutes();

    if (day !== todayET) return false; // A schedule cannot be running today if it is set for a different day.

    const enableMins = sched.enableTime
        ? sched.enableTime.split(":").map(Number).reduce((h, m) => h * 60 + m, 0)
        : 0;

    const disableMins = sched.disableTime
        ? sched.disableTime.split(":").map(Number).reduce((h, m) => h * 60 + m, 0)
        : 1440;

    return currentMins >= enableMins && currentMins < disableMins;
};